import { Toaster } from '@nexus/ui';
import type { Locale } from '@nexus/core';
import { useQuery } from '@tanstack/react-query';
import { Outlet } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { browserLocale } from '../i18n';
import { sessionQuery } from '../session/session';
import { settingsQuery } from '../session/settings';
import {
  storedAppearance,
  useApplyAppearance,
  useBrandColor,
  useResolvedTheme,
  type Appearance,
} from '../theme/appearance';

/**
 * Applies appearance and language: from the personal settings once logged in, before that from
 * the last values on this device. Reads only the cache, the app area loads the data.
 */
export function RootLayout() {
  const { i18n } = useTranslation();
  const settings = useQuery({ ...settingsQuery, enabled: false }).data?.values;
  const company = useQuery({ ...sessionQuery, enabled: false }).data?.company;
  const [stored] = useState(storedAppearance);

  const appearance: Appearance = {
    theme:
      (settings?.['core.theme'] as Appearance['theme'] | undefined) ?? stored.theme ?? 'system',
    density:
      (settings?.['core.density'] as Appearance['density'] | undefined) ??
      stored.density ??
      'comfortable',
    motion:
      (settings?.['core.motion'] as Appearance['motion'] | undefined) ?? stored.motion ?? 'system',
    locale: (settings?.['core.locale'] as Locale | undefined) ?? stored.locale ?? browserLocale(),
  };
  useApplyAppearance(appearance);
  const theme = useResolvedTheme(appearance.theme);
  useBrandColor(company?.primaryColor, theme);

  useEffect(() => {
    if (i18n.language !== appearance.locale) void i18n.changeLanguage(appearance.locale);
  }, [i18n, appearance.locale]);

  return (
    <>
      <Outlet />
      <Toaster theme={theme} />
    </>
  );
}
